// /signup
//
// Public, full-page signup. Replaces the older "switch to signup tab"
// inside ClubbedAuthModal — the modal is now login-only, and "New to
// AUMHUM? Create an account" navigates here.
//
// Collects the same shape as the care-prospect OTP step (name, mobile
// with country code, email, age, gender) but does NOT call /initialize
// — a fresh signup is just a ROLE_USER account. The post-signup
// /welcome page orchestrates whether the user becomes a patient
// prospect, stays a wellness user, or jumps to the clinician
// application flow.
//
// Country dropdown uses react-phone-input-2 — same widget the login
// modal uses, so visitors who pop between login and signup get a
// consistent experience and we get full ITU country coverage with a
// searchable list out of the box, rather than a hand-curated 12-row
// <select>.
//
// Email signup: opt-in via ?testmode=email on the URL. The intent is
// QA / scripted testing — production sign-ups go through WhatsApp.
// The email path posts to /api/user/auth/email/otp/{send,verify} with
// allowRegistration=true; the backend creates a fresh ROLE_USER if
// the email is unknown.
//
// Existing-account handling: WhatsApp OTP with allowRegistration=true
// auto-issues a token for an already-registered mobile, so users who
// were really trying to log in get persisted to the same account they
// always had. We detect that (authorities carry roles beyond
// ROLE_USER) and route to their natural home rather than dragging them
// through the welcome orchestration meant for genuinely new accounts.
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { isValidPhoneNumber } from 'react-phone-number-input'

import {
  Card,
  Container,
  Eyebrow,
  Lede,
} from './sections'
import { ArrowRight, MessageCircle, ShieldCheck } from './icons'
import {
  sendProspectOtp,
  verifyProspectOtp,
  getStoredAuthorities,
  signupPatientProspect,
} from 'src/consumer/services/patientProspectService'
import {
  sendEmailLoginOtp,
  verifyEmailLoginOtp,
} from 'src/consumer/services'
import {
  setAccessTokenExpiry,
} from 'src/consumer/services/localstorageServices'
import { executeRecaptchaV3 } from 'src/views/applyPublic/recaptchaV3'
import { persistPortalAvailabilityOverridesFromAuthData } from 'src/shared/config/portalAvailabilityRules'

const GENDER_OPTIONS = [
  { id: 'FEMALE', label: 'Female' },
  { id: 'MALE', label: 'Male' },
  { id: 'OTHER', label: 'Other' },
  { id: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say' },
]

// Persist demographics the user typed into a sessionStorage hand-off
// so /welcome can pre-fill the patient-prospect /initialize call without
// asking them again. Keyed by user mobile so a stale draft from a
// different account can't bleed across.
const SIGNUP_PROFILE_KEY = 'aumhumSignupProfileDraft'

function persistProfileDraft(profile) {
  try {
    window.sessionStorage.setItem(
      SIGNUP_PROFILE_KEY,
      JSON.stringify({ ...profile, savedAt: Date.now() }),
    )
  } catch {
    // ignore
  }
}

function clearSignupProfileDraft() {
  try {
    window.sessionStorage.removeItem(SIGNUP_PROFILE_KEY)
  } catch {
    // ignore
  }
}

function naturalHomeForAuthorities(authorities) {
  const auths = Array.isArray(authorities) ? authorities : []
  if (auths.includes('ROLE_PATIENT')) return '/patient-home'
  if (auths.includes('ROLE_PATIENT_PROSPECT')) return '/care/status'
  if (auths.includes('ROLE_PROFESSIONAL_USER')) return '/'
  return '/'
}

function isSafeInternalPath(path) {
  if (!path || typeof path !== 'string') return false
  if (!path.startsWith('/')) return false
  if (path.startsWith('//')) return false
  if (/^https?:\/\//i.test(path)) return false
  return true
}

function getAssessmentSignupTarget(searchParams) {
  const redirect = searchParams.get('redirect') || ''
  const intent = searchParams.get('intent') || ''
  const assessmentSlug = searchParams.get('assessmentSlug') || ''
  if (isSafeInternalPath(redirect) && (
    intent === 'assessment' ||
    redirect.startsWith('/assessment/') ||
    redirect.startsWith('/assessments/') ||
    redirect.startsWith('/patient-home/assessments/')
  )) {
    return redirect
  }
  if (intent === 'assessment' && assessmentSlug) {
    return `/assessment/${encodeURIComponent(assessmentSlug)}`
  }
  return ''
}

function buildLoginHref(searchParams) {
  const redirect = searchParams.get('redirect') || ''
  const params = new URLSearchParams({ lt: '1' })
  if (isSafeInternalPath(redirect)) {
    params.set('redirect', redirect)
  }
  return `/?${params.toString()}`
}

// Persist auth fields from an email-OTP verify response so the rest of
// the app sees the user as logged in. Mirrors the wellness OTP login's
// `handleAfterLoginCAMC` first three lines (token + expiry + authorities).
function persistEmailAuthSession(data) {
  if (!data?.accessToken) return
  try {
    window.localStorage.setItem('token', data.accessToken)
    if (typeof data.accessTokenExpiry !== 'undefined') {
      // The email/otp/verify endpoint returns expiry in seconds; the
      // wellness flow expects ms. Normalise to whatever the helper
      // expects (it stores as-is).
      setAccessTokenExpiry({ accessTokenExpiry: data.accessTokenExpiry })
    }
    if (Array.isArray(data.authorities)) {
      window.localStorage.setItem('authorities', JSON.stringify(data.authorities))
    }
    persistPortalAvailabilityOverridesFromAuthData(data)
    if (typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('aumhum:auth-changed'))
    }
  } catch (e) {
    console.warn('Failed to persist email auth session:', e)
  }
}

export default function SignupView() {
  const [searchParams] = useSearchParams()
  // Test/QA opt-in: ?testmode=email switches the form from the
  // WhatsApp OTP path to email OTP. Production signups should always
  // go through WhatsApp; email path is scripted-test-friendly because
  // it can't be SIM-blocked and works in CI environments without a
  // mobile.
  const testEmailSignup = searchParams.get('testmode') === 'email'
  const assessmentSignupTarget = useMemo(
    () => getAssessmentSignupTarget(searchParams),
    [searchParams],
  )
  const loginHref = useMemo(
    () => buildLoginHref(searchParams),
    [searchParams],
  )

  const [name, setName] = useState('')
  const [mobile, setMobile] = useState('') // E.164 digits-only WITHOUT leading + (react-phone-input-2 convention)
  const [email, setEmail] = useState('')
  const [age, setAge] = useState('')
  const [gender, setGender] = useState('')

  const [otp, setOtp] = useState('')
  const [channel, setChannel] = useState('whatsapp') // whatsapp | email
  const [sessionId, setSessionId] = useState(null)
  const [otpSent, setOtpSent] = useState(false)
  const [resendIn, setResendIn] = useState(0)
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const tickRef = useRef(null)

  useEffect(() => {
    if (resendIn <= 0) {
      if (tickRef.current) clearInterval(tickRef.current)
      tickRef.current = null
      return undefined
    }
    tickRef.current = setInterval(
      () => setResendIn((s) => Math.max(0, s - 1)),
      1000,
    )
    return () => {
      if (tickRef.current) clearInterval(tickRef.current)
    }
  }, [resendIn])

  // E.164 form: react-phone-input-2 emits digits-only without the
  // leading +. The wellness OTP backend expects "+digits" so we add
  // the prefix when sending.
  const fullMobile = mobile ? `+${mobile}` : ''
  const validMobile = !!mobile && isValidPhoneNumber(fullMobile)

  const emailLooksOk =
    !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const ageIsValid =
    !age ||
    (/^\d{1,3}$/.test(age) && Number(age) >= 0 && Number(age) <= 120)

  const requestMobileOtp = async () => {
    setError('')
    if (!validMobile) {
      setError('Please enter a valid mobile number for the country you picked.')
      return
    }
    if (!emailLooksOk) {
      setError('That email doesn’t look right. Leave it blank if you’d rather skip.')
      return
    }
    if (!ageIsValid) {
      setError('Please enter a valid age (0–120) or leave it blank.')
      return
    }
    try {
      setSubmitting(true)
      // Run the v3 captcha challenge BEFORE the network call so the
      // backend can reject bot traffic before Twilio dispatch. Returns
      // null when the host has no site-key configured (dev / preview);
      // the backend's verifyForRequest treats that as trivially
      // successful so the dev experience is unchanged.
      const recaptchaToken = await executeRecaptchaV3('signup_otp_send')
      const res = await sendProspectOtp(fullMobile, 'whatsapp', { recaptchaToken })
      setSessionId(res.sessionId)
      setChannel(res.channel || 'whatsapp')
      setOtpSent(true)
      setResendIn(res.resendDelaySeconds || 20)
    } catch (e) {
      setError(e.message || 'Could not send the code. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // Email OTP path — only available when ?testmode=email. Wires to the
  // existing /api/user/auth/email/otp/* endpoints. The backend supports
  // allowRegistration=true so an unknown email auto-creates ROLE_USER.
  const requestEmailOtp = async () => {
    setError('')
    const trimmed = (email || '').trim()
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.')
      return
    }
    try {
      setSubmitting(true)
      // Same captcha gate as the WhatsApp path — bots fail before we
      // burn email-send quota. Token is null on hosts without a
      // site-key (dev), and the backend treats that as trivially OK.
      const recaptchaToken = await executeRecaptchaV3('signup_otp_send')
      const res = await sendEmailLoginOtp(trimmed, {
        allowRegistration: true,
        recaptchaToken,
      })
      const data = res?.data
      if (!data || !data.success) {
        const errData = res?.response?.data || data || {}
        throw new Error(errData.message || 'Could not send the code to your email.')
      }
      setSessionId(data.sessionId)
      setChannel('email')
      setOtpSent(true)
      setResendIn(data.resendDelaySeconds || 60)
    } catch (e) {
      setError(e.message || 'Could not send the code. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const verifyAndCreate = async () => {
    setError('')
    if (!consent) {
      setError('Please agree to the privacy + terms before continuing.')
      return
    }
    setSubmitting(true)
    try {
      if (channel === 'email') {
        // Email OTP verify path. Returns access token + authorities
        // directly — no separate auth-session helper to call.
        const res = await verifyEmailLoginOtp(email.trim(), otp, sessionId, { allowRegistration: true })
        const data = res?.data
        if (!data?.accessToken) {
          const errData = res?.response?.data || data || {}
          throw new Error(errData.message || 'Could not verify the code.')
        }
        persistEmailAuthSession(data)
      } else {
        // WhatsApp / mobile path. verifyProspectOtp owns the
        // auth-session persistence.
        await verifyProspectOtp({
          mobile: fullMobile,
          otp,
          sessionId,
          channel,
        })
      }

      const authorities = getStoredAuthorities()
      const profile = {
        name: name.trim() || null,
        mobile: fullMobile || null,
        email: email.trim() || null,
        age: age ? Number(age) : null,
        gender: gender || null,
      }
      persistProfileDraft(profile)

      const hasEstablishedRole = authorities.some((a) => a !== 'ROLE_USER')

      // Returning user (existing patient/prospect/professional) — take
      // them straight to whichever portal matches their highest role.
      // We do NOT re-run the prospect-create call for these users —
      // they're already past the prospect stage, or have a different
      // primary identity (clinician).
      if (hasEstablishedRole) {
        window.location.assign(
          assessmentSignupTarget || naturalHomeForAuthorities(authorities),
        )
        return
      }

      // Special case kept from the previous behaviour: if /signup was
      // entered with ?intent=assessment&assessmentSlug=…, the user was
      // mid-assessment and just needed an account to save their result.
      // Create a Patient Prospect (assessment results live in prospect
      // scope) and bounce them back to the assessment.
      if (assessmentSignupTarget) {
        await signupPatientProspect({
          name: profile.name,
          mobile: profile.mobile,
          email: profile.email,
          age: profile.age,
          gender: profile.gender,
          pathway: 'ASSESSMENT',
          specialtyTag: 'triage',
          subjectType: 'SELF',
          source: 'WEB',
          bookingIntent: 'assessment',
        })
        clearSignupProfileDraft()
        window.location.assign(assessmentSignupTarget)
        return
      }

      // Care-by-default fresh signup. The home page positions
      // overwhelmingly as Clinical mental healthcare, so the most
      // likely intent for a brand-new account is care. Create a
      // Patient Prospect with the ASSESSMENT placeholder pathway
      // (idempotent — the user can re-pick their actual pathway on
      // /care/status's progressive intake form) and route them
      // straight to the prospect portal. Visitors who actually wanted
      // the wellness app or are clinicians see opt-out paths from
      // /care/status (wellness escape link) and from the bottom of
      // the /signup form (clinician /apply link), so the default
      // doesn't trap anyone.
      try {
        const session = await signupPatientProspect({
          name: profile.name,
          mobile: profile.mobile,
          email: profile.email,
          age: profile.age,
          gender: profile.gender,
          pathway: 'ASSESSMENT',
          specialtyTag: 'triage',
          subjectType: 'SELF',
          source: 'WEB',
          bookingIntent: 'signup',
        })
        clearSignupProfileDraft()
        // existingPatientPath=true means the caller was already a
        // ROLE_PATIENT — backend logged the request but skipped the
        // prospect role/record. Route to /patient-home rather than
        // dropping them on the prospect dashboard.
        if (session?.existingPatientPath) {
          window.location.assign('/patient-home?newCareRequest=1')
          return
        }
        // Preserve `?fromAssessment=1` so /care/status knows the new
        // user just finished an assessment — it'll replay the take from
        // sessionStorage against the authenticated endpoint and anchor
        // the intake form so they can share more details right away.
        const carryFromAssessment =
          typeof window !== 'undefined' &&
          new URLSearchParams(window.location.search).get('fromAssessment') === '1'
        window.location.assign(
          carryFromAssessment ? '/care/status?fromAssessment=1' : '/care/status',
        )
      } catch (prospectError) {
        // /initialize can fail (4xx for malformed input, 5xx for
        // backend issues, network glitch, etc.). The user IS
        // authenticated at this point — OTP verify already persisted
        // their token. Don't strand them on the signup form. Hand off
        // to /portals (the canonical portal-selector page; /welcome is
        // a backward-compat alias) with the failure reason; that page
        // has the 3-way fork (care / wellness / clinician) as a safety
        // net. The user can pick "I want care" again to retry, or
        // pick a different path entirely.
        const reason = prospectError?.message || 'prospect_init_failed'
        // Don't throw — we WANT the OTP-verify success state
        // preserved (the user is logged in). Just nav to the
        // fallback.
        window.location.assign(
          `/portals?fresh=1&prospectFailed=1&reason=${encodeURIComponent(reason)}`,
        )
      }
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.')
      setSubmitting(false)
    }
  }

  const heroNote = useMemo(
    () => (
      <ul className="as-care-hero-trust" aria-label="What signing up gets you">
        <li className="as-trust-item">
          <ShieldCheck /> Confidential
        </li>
        <li className="as-trust-item">
          <MessageCircle />{' '}
          {testEmailSignup ? 'One-time code by email' : 'One-time WhatsApp code'}
        </li>
      </ul>
    ),
    [testEmailSignup],
  )

  const sendButtonDisabled = testEmailSignup
    ? !email || !emailLooksOk || submitting
    : !validMobile || submitting

  return (
    <main id="main">
      <section className="as-care-hero as-care-hero--centered">
        <Container>
          <div className="as-care-hero-stack">
            <Eyebrow>Create your AUMHUM account</Eyebrow>
            <h1 className="as-h1 as-care-hero-title">
              Sign up in{' '}
              <span className="as-brand-gradient">under a minute.</span>
            </h1>
            <Lede className="as-care-hero-lede">
              We&rsquo;ll verify your{' '}
              {testEmailSignup ? 'email' : 'mobile'}, set up your free account,
              and ask what you want to do next — get matched with a specialist,
              use the wellness app, or apply to join as a clinician.
            </Lede>

            {testEmailSignup ? (
              <div
                role="note"
                className="sv-test-mode-banner"
                aria-label="Test mode active"
              >
                <span aria-hidden>🧪</span> Test mode — email OTP path. Production
                signups go through WhatsApp.
              </div>
            ) : null}

            <Card className="pps-card-form sv-card" emphasised>
              <div className="pps-field">
                <label className="pps-field-label" htmlFor="sv-name">
                  Your name <span className="pps-field-optional">(optional)</span>
                </label>
                <input
                  id="sv-name"
                  className="pps-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="First name is fine"
                  autoComplete="given-name"
                  disabled={otpSent && !error}
                />
              </div>

              {/* Mobile field — always visible. Even in email test mode
                  we still capture the mobile for downstream use; just
                  the OTP channel is email. */}
              <div className="pps-field">
                <label className="pps-field-label" htmlFor="sv-mobile">
                  Mobile number{' '}
                  {testEmailSignup ? (
                    <span className="pps-field-optional">(optional in test mode)</span>
                  ) : null}
                </label>
                <div className="sv-phoneinput-wrap">
                  <PhoneInput
                    inputProps={{ id: 'sv-mobile', name: 'mobile', autoComplete: 'tel' }}
                    country={'in'}
                    preferredCountries={['in', 'us', 'gb', 'ae', 'sg', 'au', 'ca']}
                    value={mobile}
                    onChange={(v) => setMobile(v)}
                    enableSearch
                    disabled={otpSent && !error}
                    inputStyle={{ width: '100%' }}
                    containerStyle={{ width: '100%' }}
                    dropdownStyle={{ zIndex: 99999 }}
                  />
                </div>
                <span className="pps-help">
                  {testEmailSignup
                    ? 'Optional in test mode — used for your profile but not for verification.'
                    : 'We’ll send a one-time code on WhatsApp.'}
                </span>
              </div>

              <div className="pps-field">
                <label className="pps-field-label" htmlFor="sv-email">
                  Email{' '}
                  <span className="pps-field-optional">
                    {testEmailSignup ? '(used for verification)' : '(optional)'}
                  </span>
                </label>
                <input
                  id="sv-email"
                  className="pps-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  disabled={otpSent && !error}
                />
              </div>

              <div className="pps-field-row">
                <div className="pps-field pps-field--half">
                  <label className="pps-field-label" htmlFor="sv-age">
                    Your age <span className="pps-field-optional">(optional)</span>
                  </label>
                  <input
                    id="sv-age"
                    className="pps-input"
                    type="number"
                    min={0}
                    max={120}
                    value={age}
                    onChange={(e) => setAge(e.target.value.replace(/\D/g, ''))}
                    placeholder="e.g. 32"
                    autoComplete="off"
                    inputMode="numeric"
                    disabled={otpSent && !error}
                  />
                </div>

                <div className="pps-field pps-field--half">
                  <span className="pps-field-label">
                    Your gender <span className="pps-field-optional">(optional)</span>
                  </span>
                  <div className="pps-chip-row" role="radiogroup" aria-label="Gender">
                    {GENDER_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        role="radio"
                        aria-checked={gender === opt.id}
                        className={`pps-chip pps-chip--small${gender === opt.id ? ' is-active' : ''}`}
                        onClick={() => setGender((cur) => (cur === opt.id ? '' : opt.id))}
                        disabled={otpSent && !error}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {!otpSent ? (
                <div className="pps-cta-row">
                  <Link to="/" className="pps-btn is-ghost">
                    Cancel
                  </Link>
                  <button
                    type="button"
                    className={`pps-btn is-primary${!sendButtonDisabled ? '' : ' is-disabled'}`}
                    disabled={sendButtonDisabled}
                    onClick={testEmailSignup ? requestEmailOtp : requestMobileOtp}
                  >
                    {submitting
                      ? 'Sending…'
                      : testEmailSignup
                        ? (<>Send code to email <ArrowRight /></>)
                        : (<>Send WhatsApp code <MessageCircle /></>)}
                  </button>
                </div>
              ) : (
                <>
                  <div className="pps-field">
                    <label className="pps-field-label" htmlFor="sv-otp">
                      Enter the code we sent on{' '}
                      {channel === 'email' ? 'email' : channel === 'sms' ? 'SMS' : 'WhatsApp'}
                    </label>
                    <div className="pps-otp-row">
                      <input
                        id="sv-otp"
                        className="pps-input"
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        /* Production OTPs are always 6 digits; the
                           Control Center test-OTP-mobile whitelist
                           lets operators set codes anywhere in 4–8
                           digits. Accept the full backend-allowed
                           range so test sign-ins aren't truncated. */
                        maxLength={8}
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                        placeholder="••••••"
                        autoComplete="one-time-code"
                      />
                    </div>
                    <div className="pps-channel-toggle">
                      {resendIn > 0 ? (
                        <span>Resend in {resendIn}s</span>
                      ) : (
                        <button
                          type="button"
                          onClick={testEmailSignup ? requestEmailOtp : requestMobileOtp}
                        >
                          Resend code
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="pps-checkbox-row">
                    <input
                      id="sv-consent"
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                    />
                    <label htmlFor="sv-consent">
                      I agree to AUMHUM&rsquo;s{' '}
                      <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>{' '}
                      and{' '}
                      <a href="/terms" target="_blank" rel="noreferrer">Terms</a>.
                    </label>
                  </div>

                  <div className="pps-cta-row">
                    <button
                      type="button"
                      className="pps-btn is-ghost"
                      onClick={() => {
                        setOtpSent(false)
                        setOtp('')
                      }}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      className={`pps-btn is-primary${otp.length >= 4 && consent && !submitting ? '' : ' is-disabled'}`}
                      disabled={otp.length < 4 || !consent || submitting}
                      onClick={verifyAndCreate}
                    >
                      {submitting ? 'Creating…' : (<>Create account <ArrowRight /></>)}
                    </button>
                  </div>
                </>
              )}

              {error ? (
                <div className="pps-error" role="alert">{error}</div>
              ) : null}
            </Card>

            {heroNote}

            <p className="sv-login-link">
              Already have an account?{' '}
              <Link to={loginHref}>Log in</Link>
            </p>

            {/* Other-role signup affordances. The default /signup flow
                creates a Patient Prospect (care-by-default) — these
                links exist so visitors who are NOT signing up for care
                land on the right entry point without needing to fill
                the wrong form first.
                  - Practitioners (doctors, therapists, psychologists,
                    nutritionists, etc.) → /apply, which runs the
                    practitioner-prospect lifecycle
                    (ROLE_CREATOR/ROLE_TEACHER funnel) with its own
                    OTP, CV upload, and approval pipeline.
                  - Wellness-only users (the AUMHUM app, no care) get
                    their escape from /care/status's empty state,
                    which is where they'll land on the next page —
                    no need to surface it here too.
                The clinician link is intentionally one quiet line so
                it doesn't compete with the primary care signup. */}
            <p className="sv-other-role-link">
              Are you a doctor, therapist, psychologist, or other care
              provider?{' '}
              <a href="/apply">Apply to join AUMHUM&rsquo;s care network →</a>
            </p>

            {/* Google reCAPTCHA terms-of-use disclosure. Required when
                using the invisible v3 challenge — Google's branding
                guidelines say either the badge stays visible OR you
                ship a textual disclosure with both linked policies.
                The badge is hidden by global CSS (`.grecaptcha-badge`),
                so this small line satisfies the TOU. */}
            <p className="sv-recaptcha-notice">
              This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
                Terms of Service
              </a>{' '}
              apply.
            </p>
          </div>
        </Container>
      </section>
    </main>
  )
}
